import SEO from "@/components/SEO";
import { BRAND } from "@/brand";
import { Link } from "react-router-dom";

const COMMITMENTS = [
  { img: "/jumbo/images/Group-9181.svg", title: "Plain-English pricing", body: "We publish per-brand payout rates publicly. The number at submission is the number we pay on a verified balance — no haggling, no deductions." },
  { img: "/jumbo/images/customizable-icon.svg", title: "Human verification", body: "Every submission ends with a real reviewer before payout is approved. We don't buy cards we can't verify or believe were acquired fraudulently." },
  { img: "/jumbo/images/browser-icon.svg", title: "Transparent timelines", body: "Most cards clear within eight hours. You can reopen any order by reference or email and see exactly where it stands, right now." },
];

const NUMBERS = [
  { top: "50,000+", label: "Cards purchased since 2023" },
  { top: "$5.2M", label: "Paid out to sellers" },
  { top: "4.9 / 5", label: "Average review score" },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About GC SPARK"
        description={`Who we are, where we operate, and how we keep gift card payouts straightforward, transparent, and safe. ${BRAND.operatingRegion}.`}
        path="/about"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
      />

      {/* Two-column intro hero */}
      <section className="search-home-hero-section" data-testid="about-hero" style={{ paddingBottom: 40 }}>
        <div className="main-container w-container">
          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 60, alignItems: "center" }} className="about-hero-grid">
            <div>
              <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 18 }}>
                About GC SPARK
              </div>
              <h1 className="h1 _700-bold" style={{ marginBottom: 22 }}>
                A trade desk for the gift cards stuck in your <span style={{ color: "#346af8" }}>drawer</span>
              </h1>
              <p style={{ fontSize: 18, color: "#4a4d5f", lineHeight: 1.6, marginBottom: 26 }}>
                GC SPARK is a US-only marketplace built by a small team who got tired of watching gift cards expire. We convert unused retail cards into cash via PayPal, Cash App, Zelle or Chime — transparent rates, private data, human review.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Link to="/submit" className="primary-button w-button" data-testid="about-cta-sell">
                  Sell a card
                </Link>
                <Link to="/how-it-works" className="btn w-inline-block">
                  <div className="btn-text">How it works</div>
                </Link>
              </div>
            </div>
            <div>
              <img src="/jumbo/images/testimonial-image-min.png" loading="lazy" alt="GC SPARK team"
                style={{ width: "100%", height: "auto", borderRadius: 24, boxShadow: "0 40px 80px -30px rgba(41,45,85,.4)" }} />
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) { .about-hero-grid { grid-template-columns: 1fr !important; gap: 30px !important; } }
        `}</style>
      </section>

      {/* Big numbers row */}
      <section className="company-rating-section" style={{ paddingTop: 40, paddingBottom: 40 }} data-testid="about-numbers">
        <div className="main-container w-container">
          <div className="company-rating-container">
            <div className="company-rating-divs">
              {NUMBERS.map((n) => (
                <div className="company-rating-wrapper" key={n.label}>
                  <div className="rating-numbers-wrapper"><div>{n.top}</div></div>
                  <div className="rating-source"><div>{n.label}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Commitments as security-style cards */}
      <section className="security-section" data-testid="about-commitments">
        <div className="main-container">
          <div className="security-container">
            <div className="security-heading">
              <h2 className="h2 _700-bold">Three promises we hold ourselves to</h2>
            </div>
            <div className="security-card-wrapper">
              {COMMITMENTS.map((c, i) => (
                <div className={`security-card${i === 1 ? " middle-block" : ""}`} key={c.title}>
                  <div className="security-card-image">
                    <img src={c.img} loading="lazy" alt={c.title} />
                  </div>
                  <div className="security-card-title"><div className="h5 _700-bold">{c.title}</div></div>
                  <div className="security-card-para"><div>{c.body}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brand independence + registered entity */}
      <section className="changelog-section" data-testid="about-brand-relationships" style={{ paddingTop: 60, paddingBottom: 60 }}>
        <div className="main-container w-container">
          <div className="changelog-blocks-container">
            <div className="changelog-heading-container">
              <h2 className="h3 _700-bold">Brand relationships & registered entity</h2>
            </div>
            <div className="template-block" style={{ padding: 32, borderRadius: 20, background: "#fff", border: "1px solid #eaeaf0", marginTop: 20 }}>
              <p className="changelog-para" style={{ lineHeight: 1.7, color: "#4a4d5f" }}>
                GC SPARK is an independent online gift card exchange platform owned and
                operated by {BRAND.ownerName} in {BRAND.ownerCountry}. We provide a platform
                for customers to submit eligible gift cards for verification and exchange
                them for cash.
              </p>
              <p className="changelog-para" style={{ lineHeight: 1.7, color: "#4a4d5f", marginTop: 16 }}>
                Each submitted gift card is reviewed for validity and eligibility before
                payment is issued. Processing and payment times may vary depending on the
                gift card, verification requirements, and transaction circumstances.
              </p>
              <p className="changelog-para" style={{ lineHeight: 1.7, color: "#4a4d5f", marginTop: 16 }}>
                GC SPARK is not owned, operated, endorsed, sponsored, or officially
                affiliated with the gift card brands listed on our website. Brand names and
                logos are displayed solely to identify the gift cards accepted on the
                platform.
              </p>
              <div style={{ marginTop: 24, paddingTop: 24, borderTop: "1px solid #eaeaf0" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#9a93a6", marginBottom: 6 }}>Customer support</div>
                <a href={`mailto:${BRAND.supportEmail}`} style={{ color: "#346af8", fontWeight: 600 }}>{BRAND.supportEmail}</a>
                <div style={{ color: "#4a4d5f", fontSize: 14, marginTop: 4 }}>Available 24/7 via email and live chat.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="boss-section" data-testid="about-cta">
        <div className="boss-container">
          <div className="boss-cta-band">
            <h2>Try us with one card</h2>
            <p>See the rate, submit in three minutes, and get paid after verification — typically within 24 hours of approval.</p>
            <Link to="/submit" className="boss-btn" data-testid="about-cta-bottom">Sell my first card</Link>
          </div>
        </div>
      </section>
    </>
  );
}
